<template>
  <div class="calendar-controls mb-3 d-flex align-items-center">
    <div v-if="!isToday" class="calendar-controls__nav">
      <div class="d-flex align-items-center">
        <action-button
          :disabled="isToday"
          variant="light px-3 mr-2"
          i="fas fa-chevron-left"
          @click="pager('back')"
        />
        <action-button
          :disabled="isToday"
          variant="light px-3"
          i="fas fa-chevron-right"
          @click="pager('forward')"
        />
      </div>
    </div>
    <div class="calendar-controls__label">
      <div ref="pickerWrapper" class="mode-label-wrapper">
        <button
          type="button"
          class="mode-label-container d-flex align-items-center justify-content-center p-2 rounded font-weight-bold"
          :class="{ 'mode-label-container--static': isToday }"
          @click="togglePicker"
        >
          <span>{{ label }}</span>
          <i
            v-if="!isToday"
            class="fas ml-2"
            :class="showPicker ? 'fa-chevron-down' : 'fa-chevron-up'"
          />
        </button>

        <div
          v-if="showPicker && !isToday"
          class="calendar-picker"
          :class="{ 'calendar-picker--month': isMonth }"
        >
          <template v-if="isMonth">
            <div class="calendar-picker__month-header">
              <button
                type="button"
                class="calendar-picker__nav"
                @click="changePickerYear(-1)"
              >
                <i class="fas fa-angle-double-left" />
              </button>
              <span>{{ pickerYear }}</span>
              <button
                type="button"
                class="calendar-picker__nav"
                @click="changePickerYear(1)"
              >
                <i class="fas fa-angle-double-right" />
              </button>
            </div>

            <div class="calendar-picker__months">
              <button
                v-for="month in monthOptions"
                :key="month.value"
                type="button"
                class="calendar-picker__month"
                :class="{
                  'calendar-picker__month--active':
                    month.value === pickerMonth && pickerYear === modelYear,
                }"
                @click="selectMonth(month.value)"
              >
                {{ month.name }}
              </button>
            </div>
          </template>

          <template v-else>
            <div class="calendar-picker__header">
              <button
                type="button"
                class="calendar-picker__nav"
                @click="changePickerMonth(-12)"
              >
                <i class="fas fa-angle-double-left" />
              </button>
              <button
                type="button"
                class="calendar-picker__nav"
                @click="changePickerMonth(-1)"
              >
                <i class="fas fa-angle-left" />
              </button>
              <span>{{ pickerMonthLabel }}</span>
              <button
                type="button"
                class="calendar-picker__nav"
                @click="changePickerMonth(1)"
              >
                <i class="fas fa-angle-right" />
              </button>
              <button
                type="button"
                class="calendar-picker__nav"
                @click="changePickerMonth(12)"
              >
                <i class="fas fa-angle-double-right" />
              </button>
            </div>

            <div
              class="calendar-picker__weekdays"
              :class="{ 'calendar-picker__weekdays--with-number': isWeek }"
            >
              <span v-if="isWeek" class="calendar-picker__week-number-header" />
              <span v-for="day in pickerWeekdays" :key="day">{{ day }}</span>
            </div>

            <div
              v-for="week in pickerWeeks"
              :key="week.key"
              class="calendar-picker__week"
              :class="{
                'calendar-picker__week--with-number': isWeek,
                'calendar-picker__week--active':
                  isWeek && isSelectedWeek(week.days[0].date),
              }"
              @click="isWeek ? selectWeek(week.days[0].date) : null"
            >
              <span v-if="isWeek" class="calendar-picker__week-number">
                {{ week.weekNumber }}
              </span>
              <button
                v-for="day in week.days"
                :key="day.key"
                type="button"
                class="calendar-picker__day"
                :class="{
                  'calendar-picker__day--muted': !day.isCurrentMonth,
                  'calendar-picker__day--active': isSelectedDay(day.date),
                  'calendar-picker__day--today': day.isToday,
                }"
                @click.stop="
                  isWeek ? selectWeek(day.date) : selectDay(day.date)
                "
              >
                {{ day.date.format('D') }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="calendar-controls__services min-w-0">
      <div
        class="overflow-auto text-nowrap d-flex align-items-center bg-light p-2 rounded"
      >
        <div
          v-for="c in categories"
          :key="c.id"
          class="service-pill mr-2 "
          :style="serviceStyle(c)"
          @click.stop="selectCategory(c)"
        >
          <span class="fas fa-folder-open"></span>
          <span class="px-2 mr-2">
            {{ c.name }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import ActionButton from 'ActionButton';

import { capitalize } from '@/utils/filters';

const SERVICE_BACKGROUND_BY_ID = {
  1: '#FFF8BB',
};

export default {
  name: 'CompuCalendar',
  props: {
    categories: { type: Array, default: () => [] },
    value: { type: Date, required: true, default: () => null },
    mode: { type: String, default: 'week' },
  },
  components: {
    ActionButton,
  },
  data() {
    return {
      showPicker: false,
      pickerDate: this.value || new Date(),
      pickerWeekdays: ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'],
      monthOptions: [
        { value: 0, name: 'Enero' },
        { value: 1, name: 'Febrero' },
        { value: 2, name: 'Marzo' },
        { value: 3, name: 'Abril' },
        { value: 4, name: 'Mayo' },
        { value: 5, name: 'Junio' },
        { value: 6, name: 'Julio' },
        { value: 7, name: 'Agosto' },
        { value: 8, name: 'Septiembre' },
        { value: 9, name: 'Octubre' },
        { value: 10, name: 'Noviembre' },
        { value: 11, name: 'Diciembre' },
      ],
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    label() {
      if (this.isToday || this.isDay)
        return `${capitalize(moment(this.model).format('dddd'))} ${moment(
          this.model
        ).format('D')} de ${capitalize(
          moment(this.model).format('MMMM')
        )} ${moment(this.model).format('YYYY')}`;
      else if (this.isWeek) {
        const start = moment(this.model).startOf('isoWeek');
        const end = moment(this.model).endOf('isoWeek');

        return `${capitalize(start.format('dddd'))} ${start.format(
          'D'
        )} ${start.format('MMM')} - ${capitalize(
          end.format('dddd')
        )} ${end.format('D')} ${end.format('MMM')} ${end.format('YYYY')}`;
      } else if (this.isMonth) {
        return capitalize(moment(this.model).format('MMMM YYYY'));
      }
    },
    isToday() {
      return this.mode === 'today';
    },
    isDay() {
      return this.mode === 'day';
    },
    isWeek() {
      return this.mode === 'week';
    },
    isMonth() {
      return this.mode === 'month';
    },
    pickerMoment() {
      return moment(this.pickerDate);
    },
    pickerMonthLabel() {
      return `${capitalize(
        this.pickerMoment.format('MMMM')
      )} ${this.pickerMoment.format('YYYY')}`;
    },
    pickerYear() {
      return this.pickerMoment.year();
    },
    pickerMonth() {
      return this.pickerMoment.month();
    },
    modelYear() {
      return moment(this.model).year();
    },
    pickerWeeks() {
      const start = moment(this.pickerDate)
        .startOf('month')
        .startOf('isoWeek');
      const end = moment(this.pickerDate)
        .endOf('month')
        .endOf('isoWeek');
      const weeks = [];
      const current = moment(start);

      while (current.isSameOrBefore(end, 'day')) {
        const weekStart = moment(current);
        const days = [];

        for (let index = 0; index < 7; index += 1) {
          const day = moment(current);

          days.push({
            date: day,
            key: day.format('YYYY-MM-DD'),
            isCurrentMonth: day.isSame(this.pickerDate, 'month'),
            isToday: day.isSame(moment(), 'day'),
          });

          current.add(1, 'day');
        }

        weeks.push({
          key: weekStart.format('YYYY-MM-DD'),
          weekNumber: weekStart.isoWeek(),
          days,
        });
      }

      return weeks;
    },
  },
  watch: {
    model(value) {
      this.pickerDate = value || new Date();
    },
    mode() {
      this.closePicker();
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick);
  },
  methods: {
    togglePicker() {
      if (this.isToday) return;

      this.pickerDate = this.model || new Date();
      this.showPicker = !this.showPicker;
    },
    closePicker() {
      this.showPicker = false;
    },
    onDocumentClick(event) {
      if (!this.showPicker || !this.$refs.pickerWrapper) return;

      if (!this.$refs.pickerWrapper.contains(event.target)) {
        this.closePicker();
      }
    },
    changePickerMonth(amount) {
      this.pickerDate = moment(this.pickerDate)
        .add(amount, 'months')
        .toDate();
    },
    changePickerYear(amount) {
      this.pickerDate = moment(this.pickerDate)
        .add(amount, 'years')
        .toDate();
    },
    selectDay(day) {
      this.model = moment(day)
        .startOf('day')
        .toDate();
      this.closePicker();
    },
    selectWeek(weekStart) {
      this.model = moment(weekStart)
        .startOf('isoWeek')
        .toDate();
      this.closePicker();
    },
    selectMonth(month) {
      this.model = moment(this.pickerDate)
        .month(month)
        .startOf('month')
        .toDate();
      this.closePicker();
    },
    isSelectedDay(day) {
      return moment(day).isSame(this.model, 'day');
    },
    isSelectedWeek(day) {
      return moment(day).isSame(moment(this.model).startOf('isoWeek'), 'day');
    },
    pager(dir) {
      if (this.isToday) return;
      let unit = 'days';
      if (this.mode === 'week') unit = 'weeks';
      else if (this.mode === 'month') unit = 'months';
      if (dir === 'back')
        this.model = moment(this.model)
          .subtract(1, unit)
          .toDate();
      else
        this.model = moment(this.model)
          .add(1, unit)
          .toDate();
    },
    hexToRgb(hex) {
      const clean = hex.replace('#', '');
      const bigint = parseInt(clean, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return `${r}, ${g}, ${b}`;
    },
    bgWithAlpha(hex, alpha = 1) {
      try {
        return `rgba(${this.hexToRgb(hex)}, ${alpha})`;
      } catch (e) {
        return hex;
      }
    },
    getServiceBackground(c) {
      const customBackground = SERVICE_BACKGROUND_BY_ID[Number(c.id)];

      if (customBackground && !c.selected) {
        return customBackground;
      }

      return this.bgWithAlpha(c.color, c.selected ? 1 : 0.12);
    },
    serviceStyle(c) {
      return {
        border: `1px solid ${c.color}`,
        color: c.selected ? 'white' : c.color,
        background: this.getServiceBackground(c),
        padding: '4px 8px',
        borderRadius: '18px',
      };
    },
    selectCategory(c) {
      this.$set(c, 'selected', !c.selected);
      this.$emit('select-category', c);
    },
  },
};
</script>

<style scoped>
.bg-light {
  background: #f1f6f5;
}
.calendar-controls {
  gap: 6px;
}
.calendar-controls__nav {
  flex: 0 0 auto;
}
.calendar-controls__label {
  flex: 0 1 600px;
  min-width: 280px;
}
.calendar-controls__services {
  flex: 1 1 auto;
  min-width: 0;
}
.calendar-controls__nav ::v-deep .btn,
.calendar-controls__nav ::v-deep button,
.calendar-controls__services .bg-light {
  min-height: 50px;
}
.calendar-controls__nav ::v-deep .btn,
.calendar-controls__nav ::v-deep button {
  align-items: center;
  display: inline-flex;
  justify-content: center;
}
.service-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 18px;
  padding: 4px 8px;
  cursor: pointer;
}
.mode-label-wrapper {
  position: relative;
  width: 100%;
  z-index: 5;
}
.mode-label-container {
  background: #d9e5df !important;
  color: black !important;
  border: none !important;
  width: 100% !important;
  box-shadow: none !important;
  min-height: 50px;
  white-space: nowrap;
}
.mode-label-container--static {
  cursor: default;
}
.calendar-picker {
  background: white;
  border: 1px solid #d9e5df;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  left: 50%;
  padding: 14px 12px;
  position: absolute;
  top: 100%;
  transform: translateX(-50%);
  width: 300px;
  z-index: 20;
}
.calendar-picker--month {
  padding: 16px 18px;
  width: 300px;
}
.calendar-picker__header,
.calendar-picker__month-header {
  align-items: center;
  display: grid;
  grid-template-columns: 32px 32px 1fr 32px 32px;
  margin-bottom: 12px;
  text-align: center;
}
.calendar-picker__month-header {
  grid-template-columns: 32px 1fr 32px;
}
.calendar-picker__header span,
.calendar-picker__month-header span {
  color: #11291b;
  font-weight: 700;
}
.calendar-picker__nav {
  align-items: center;
  background: #f4f6f3;
  border: none;
  border-radius: 50%;
  color: #11291b;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  width: 32px;
}
.calendar-picker__weekdays,
.calendar-picker__week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
}
.calendar-picker__weekdays {
  color: #11291b;
  font-weight: 700;
  margin-bottom: 6px;
}
.calendar-picker__weekdays--with-number,
.calendar-picker__week--with-number {
  grid-template-columns: 28px repeat(7, 1fr);
}
.calendar-picker__week {
  border-radius: 2px;
}
.calendar-picker__week--with-number:hover {
  background: #f8eded;
}
.calendar-picker__week--active {
  background: transparent;
}
.calendar-picker__week-number {
  color: #8b8b8b;
  font-weight: 700;
  line-height: 32px;
}
.calendar-picker__day,
.calendar-picker__month {
  background: transparent;
  border: 1px solid transparent;
  color: #11291b;
  font-weight: 700;
}
.calendar-picker__day {
  border-radius: 2px;
  height: 32px;
}
.calendar-picker__day--muted {
  color: #8b8b8b;
}
.calendar-picker__day--today {
  color: #ff7058;
}
.calendar-picker__day--active {
  border-color: #2db782;
}
.calendar-picker__months {
  display: grid;
  gap: 22px 12px;
  grid-template-columns: repeat(3, 1fr);
}
.calendar-picker__month {
  border-radius: 4px;
  padding: 4px 0;
}
.calendar-picker__month--active {
  background: #d9e5df;
}
.min-w-0 {
  min-width: 0;
}
</style>
